"use client";

import React, { useCallback, useContext, useState } from "react";
import styled, { css } from "styled-components";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";

import NextImage from "next/image";
import debounce from "lodash.debounce";
import NextLink from "next/link";
import { Label, P } from "@motorata/base-ui.components.atoms.typography";
import { Box, Row } from "@motorata/base-ui.components.atoms._atoms";
import { BRANDS_ARRAY, findBrand, getModelsWithBrand } from "@motorata/base-ui.data";
import { ProfileService } from "@motorata/base-ui.services.api-service";
import Spinner from "@motorata/base-ui.components.atoms.spinner";
import Link from "@motorata/next.components.atoms.link";
import { SentryService } from "@motorata/base-ui.services.sentry-service";
import Button, { IconButton } from "@motorata/base-ui.components.atoms.button";
import Icon from "../common/icon";
import { useFavourite } from "../../hooks/useFavourite";
import HamburgerMenu from "../common/HamburgerMenu";
import Image from "../common/Image";
import { useSearch } from "../../hooks/useSearch";
import LoginRegisterModal from "../modals/LoginRegisterModal";
import { useWindowSize } from "../../hooks/useWindowSize";
import { AccountContext } from "@/providers/AccountProvider";
import MobileNavigation from "@/components/layout/MobileNavigation";

const SearchRow = ({ title, link }) => {
  return (
    <NextLink href={link}>
      <SearchRowWrapper>
        <Label>{title}</Label>
      </SearchRowWrapper>
    </NextLink>
  );
};

const SearchProfile = ({ profile }) => {
  return (
    <NextLink href={`/${profile.slug}`}>
      <SearchProfileWrapper>
        <Row
          background="background"
          marginRight="1rem"
          width="40px"
          minWidth="40px"
          height="40px"
          alignItems="center"
          justifyContent="center"
          borderRadius="50%"
          overflow="hidden"
        >
          {profile.images.profile && (
            <Box width="100%" height="100%">
              <Image
                width={40}
                height={40}
                alt={`Zdjęcie profilowe ${profile.name}`}
                src={profile.images.profile.src}
                style={{
                  objectFit: "contain"
                }}
              />
            </Box>
          )}
        </Row>
        <Box>
          <Label>{profile.name}</Label>
          <Label color="third" type="third">
            @{profile.slug}
          </Label>
        </Box>
      </SearchProfileWrapper>
    </NextLink>
  );
};

const Search = ({ active = true, setActive = (e: boolean) => {}, onSelect }) => {
  const { profiles: searchProfiles, brands: searchBrands, addToSearch } = useSearch();

  const [loading, setLoading] = useState(false);

  const [profiles, setProfiles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);

  const loadItems = useCallback(async event => {
    const value = event.target.value;
    if (!value || value === "") {
      setBrands([]);
      setProfiles([]);
      return;
    }

    try {
      setLoading(true);
      // @ts-ignore
      const { content } = await ProfileService.search(value);
      setProfiles(content);
      setBrands(
        BRANDS_ARRAY.filter(el => el[0].toLowerCase().includes(value?.toLowerCase())).slice(0, 3)
      );

      setModels(getModelsWithBrand().filter(el => el.label.toLowerCase().includes(value?.toLowerCase())).slice(0, 9));
    } catch (e) {
      SentryService.error("[ERROR ProfileService.search API]", e, { value });
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedChangeHandler = useCallback(debounce(loadItems, 300), []);

  const onClick = useCallback(() => {
    setActive(false);
    onSelect(false);
  }, []);

  return (
    <>
      <Icon width="15px" icon="search" />
      <input
        placeholder="Znajdź pojazd lub profil"
        onChange={debouncedChangeHandler}
        onFocus={() => setActive(true)}
      />
      {loading && <Spinner size="15px" borderWidth={3} />}
      {active && (
        <>
          <SearchDataWrapper onClick={onClick}>
            {profiles.length > 0 && (
              <Box>
                <Label color="third" type="third" marginBottom="XS" marginTop="XS">
                  Znalezione profile
                </Label>
                {profiles.map((el, index) => (
                  <SearchProfile key={index} profile={el} />
                ))}
              </Box>
            )}
            {profiles.length === 0 && searchProfiles.length > 0 && (
              <Box>
                <Label color="third" type="third" marginBottom="XS" marginTop="XS">
                  Proponowane profile
                </Label>
                {searchProfiles.map((el, index) => (
                  <SearchProfile key={index} profile={el} />
                ))}
              </Box>
            )}
            {brands.length > 0 ? (
              <Box>
                <Label color="third" type="third" marginBottom="XS" marginTop="XS">
                  Dostępne marki
                </Label>
                {brands.map((el, index) => (
                  <SearchRow key={index} title={el[0]} link={`/oferty/all/${el[1]}`} />
                ))}
              </Box>
            ) : (
              <Box>
                <Label color="third" type="third" marginBottom="XS" marginTop="XS">
                  Proponowane marki
                </Label>
                {searchBrands.map((el, index) => {
                  const brand = findBrand(el);
                  if (brand)
                    return <SearchRow key={index} title={brand[0]} link={`/oferty/all/${brand[1]}`} />;
                })}
              </Box>
            )}
            {models.length > 0 && (
              <Box>
                <Label color="third" type="third" marginBottom="XS" marginTop="XS">
                  Dostępne modele
                </Label>
                {models.map((el, index) => (
                  <SearchRow
                    key={index}
                    title={el.label}
                    link={`/oferty/all/${el.brand}/${el.model}`}
                  />
                ))}
              </Box>
            )}
          </SearchDataWrapper>
        </>
      )}
    </>
  );
};

const SearchInputModal = ({ onSelect }) => {
  return (
    <>
      <SearchInputModalWrapper>
        <Search onSelect={onSelect} />
      </SearchInputModalWrapper>
    </>
  );
};

const SearchInput = ({ active, setActive }) => {
  return (
    <>
      <SearchInputWrapper onClick={e => e.stopPropagation()}>
        <Search active={active} setActive={setActive} onSelect={() => setActive(false)} />
      </SearchInputWrapper>
      {active && <SearchBackground onClick={() => setActive(false)} />}
    </>
  );
};

const FinanceBox = () => {
  return (
    <FinanceWrapper className="finance_wrapper">
      <FinanceContent>
        <Label type="secondary" margin="XS" textAlign="left">Rodzaje finansowania</Label>
        <NextLink href="/leasing">
          <FinanceOptionWrapper>
            <NextImage
              priority={true}
              width={40}
              height={40}
              alt={`Grafika finansowania leasing`}
              src={`/images/donut-chart.png`}
            />
            <Box marginLeft="S">
              <Label margin="0" marginBottom="XS" textAlign="left">Leasing</Label>
              <P small margin="0">Finansowanie samochodów nowych i używanych dla firm i osób prywatnych.</P>
            </Box>
          </FinanceOptionWrapper>
        </NextLink>
        <NextLink href="/kredyt">
          <FinanceOptionWrapper>
            <NextImage
              priority={true}
              width={40}
              height={40}
              alt={`Grafika finansowania kredyt`}
              src={`/images/credit-card.png`}
            />
            <Box marginLeft="S">
              <Label margin="0" marginBottom="XS" textAlign="left">Kredyt</Label>
              <P small margin="0">Podstawowa forma finansowania samochodów przez osoby prywatne.</P>
            </Box>
          </FinanceOptionWrapper>
        </NextLink>
      </FinanceContent>
    </FinanceWrapper>
  );
};

function Header() {
  const { length } = useFavourite();
  const { width } = useWindowSize();

  const { hasToken, loading, state } = useContext(AccountContext);

  const [modal, setModal] = useState<string | boolean>(false);
  const [activeSearch, setActiveSearch] = useState<string | boolean>(false);

  const [scroll, setScroll] = useState({
    isScrolled: false,
    scrollDirection: "down"
  });

  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isScrolled = -currPos.y > 20;
      const scrollDirection = currPos.y > prevPos.y ? "up" : "down";

      if (isScrolled !== scroll.isScrolled || scrollDirection !== scroll.scrollDirection) {
        setScroll({
          isScrolled,
          scrollDirection
        });
      }
    },
    [scroll]
  );

  const accountButton = loading ? <Button
    icon="plus"
    loading={true}
    onClick={() => {}}
  >
    Sprzedaj samochód
  </Button> : hasToken ? state === "account" ?
      <Link icon="user-fill" href="/konto">
        Twoje konto
      </Link>
      : <Button
        icon="user-fill"
        onClick={() =>
          setModal("account")
        }
      >
        Twoje konto
      </Button> :
    <Button
      icon="plus"
      loading={loading}
      onClick={() =>
        setModal("account")
      }
    >
      Sprzedaj samochód
    </Button>;

  return (
    <>
      <Wrapper
        show={modal || activeSearch ? true : !scroll.isScrolled || scroll.scrollDirection === "up"}
        isScrolled={scroll.isScrolled}
      >
        <Content>
          <NextLink href="/">
            <Logo>
              <NextImage
                priority={true}
                height={35}
                width={200}
                src="/images/logo.png"
                alt="Logo MotoRata"
                style={{
                  objectFit: "contain",
                  objectPosition: "left center"
                }}
              />
            </Logo>
          </NextLink>
          <SearchInput active={activeSearch} setActive={setActiveSearch} />
          <MobileButtonsWrapper>
            {/*<Box*/}
            {/*  cursor="pointer"*/}
            {/*  height="25px"*/}
            {/*  onClick={() => setModal(modal === "search" ? false : "search")}*/}
            {/*>*/}
            {/*  <Icon width="25px" icon="search" />*/}
            {/*</Box>*/}
            {
              width < 1500 && width > 900 && accountButton
            }
            <HamburgerMenu
              active={modal === "menu" || modal === "finance"}
              onClick={() => setModal(modal === "menu" || modal === "finance" ? false : "menu")}
            />
          </MobileButtonsWrapper>
          <Nav>
            {
              width < 1500 &&
              <IconButton
                size="XL"
                icon="search"
                background="none"
                onClick={() => setModal(modal === "search" ? false : "search")}
              />
            }
            <Link type="third" href="/oferty" padding="0">
              Wszystkie oferty
            </Link>
            <FinanceButton type="third" padding="0">
              Finansowanie
              <FinanceBox />
            </FinanceButton>
            {/*<Link type="third" href="/wiadomosci" padding="0">*/}
            {/*  <StyledHeart>*/}
            {/*    <Icon icon="message" />*/}
            {/*  </StyledHeart>*/}
            {/*  Wiadomości*/}
            {/*</Link>*/}
            <Link type="third" href="/obserwowane" padding="0">
              <StyledHeart>
                <Icon icon="heart" />
                {length > 0 && <span>{length}</span>}
              </StyledHeart>
              Obserwowane
            </Link>
            {accountButton}
          </Nav>
        </Content>
      </Wrapper>
      <MobileNav show={modal === "menu"}>
        <Box paddingLeft="S" paddingRight="S">
          <Link type="third" href="/oferty" margin="20px 0" onClick={() => setModal(false)}>
            Wszystkie oferty
          </Link>
          <Button type="third" margin="20px 0" onClick={() => setModal("finance")}>
            Finansowanie
          </Button>
          {/*<Link type="third" href="/wiadomosci" margin="20px 0" onClick={() => setModal(false)}>*/}
          {/*  <StyledHeart>*/}
          {/*    <Icon icon="message" />*/}
          {/*  </StyledHeart>*/}
          {/*  Wiadomości*/}
          {/*</Link>*/}
          <Link type="third" href="/obserwowane" margin="20px 0" onClick={() => setModal(false)}>
            <StyledHeart>
              <Icon icon="heart" />
              {length > 0 && <span>{length}</span>}
            </StyledHeart>
            Obserwowane
          </Link>
          <Link type="third" href="/blog" margin="20px 0" onClick={() => setModal(false)}>
            Blog
          </Link>
          {hasToken ? (
            <Link icon="user-fill" width="100%" href="/konto" onClick={() => setModal(false)}>
              Twoje konto
            </Link>
          ) : (
            <Button
              icon="plus"
              width="100%"
              loading={loading}
              onClick={() =>
                setModal("account")
              }
            >
              Sprzedaj samochód
            </Button>
          )}
        </Box>
      </MobileNav>
      <MobileNav show={modal === "finance"}>
        <div>
          <Box paddingTop="20px" paddingLeft="20px" paddingRight="20px">
            <Label type="secondary" margin="XS" textAlign="left">Rodzaje finansowania</Label>
            <NextLink href="/leasing" onClick={() => setModal(false)}>
              <Row marginTop="M" marginBottom="M">
                <NextImage
                  priority={true}
                  width={40}
                  height={40}
                  alt={`Grafika finansowania leasing`}
                  src={`/images/donut-chart.png`}
                />
                <Box marginLeft="S">
                  <Label margin="0" marginBottom="XS" textAlign="left">Leasing</Label>
                  <P small margin="0">Finansowanie samochodów nowych i używanych dla firm i osób prywatnych.</P>
                </Box>
              </Row>
            </NextLink>
            <NextLink href="/kredyt" onClick={() => setModal(false)}>
              <Row>
                <NextImage
                  priority={true}
                  width={40}
                  height={40}
                  alt={`Grafika finansowania kredyt`}
                  src={`/images/credit-card.png`}
                />
                <Box marginLeft="S">
                  <Label margin="0" marginBottom="XS" textAlign="left">Kredyt</Label>
                  <P small margin="0">Podstawowa forma finansowania samochodów przez osoby prywatne.</P>
                </Box>
              </Row>
            </NextLink>
          </Box>
        </div>
      </MobileNav>
      <MobileNav show={modal === "search"}>
        <SearchInputModal onSelect={() => setModal(false)} />
      </MobileNav>
      <LoginRegisterModal modal={modal === "account"} setModal={setModal} />
      <MobileNavigation setModal={setModal} />
    </>
  );
}

Header.propTypes = {};

export default Header;

const SearchRowWrapper = styled.div`
  padding: 5px 20px;
  width: calc(100% + 40px);
  margin-left: -20px;

  &:hover {
    background: ${({ theme }) => theme.color.background};
  }
`;

const SearchProfileWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 20px;
  width: calc(100% + 40px);
  margin-left: -20px;

  &:hover {
    background: ${({ theme }) => theme.color.background};
  }
`;

const SearchBackground = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 998;
  animation: opacityAnim 0.3s;

  @keyframes opacityAnim {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 1500px) {
    display: none;
  }
`;

const SearchDataWrapper = styled.div`
  background: white;
  padding: 5px 20px;
  width: 100%;
  position: absolute;
  left: 0;
  top: 100%;
  border-radius: 5px;
  animation: opacityAnim 0.3s;

  @keyframes opacityAnim {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  };
`;

const SearchInputModalWrapper = styled.div`
  position: relative;

  input {
    font-size: ${({ theme }) => theme.font.L};
    font-weight: ${({ theme }) => theme.font.light};
    border: 0;
    border-bottom: 1px solid ${({ theme }) => theme.color.fourth};
    padding: 10px 43px;
    width: 100%;
  }

  svg {
    position: absolute;
    left: 15px;
    top: 15px;
  }

  .spinner_wrapper {
    position: absolute;
    right: 15px;
    top: 15px;
    height: 15px;
    width: 15px;
  }
`;

const FinanceButton = styled(Button)`
  &:hover {
    .finance_wrapper {
      display: block;
    }
  }
`;

const FinanceOptionWrapper = styled(Box)`
  background: rgba(255, 255, 255, 0);
  padding: ${({ theme }) => theme.space.S};
  transition: background-color 0.3s;
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  display: flex;

  &:hover {
    background: ${({ theme }) => theme.color.background};
  }
`;

const FinanceWrapper = styled(Box)`
  padding-top: ${({ theme }) => theme.space.S};
  display: none;
  position: absolute !important;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 100%);
`;

const FinanceContent = styled(Box)`
  background: white;
  padding: ${({ theme }) => theme.space.S};
  box-shadow: ${({ theme }) => theme.variable.boxShadow};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  width: 400px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  z-index: 999;
  flex: 1 1;
  max-width: 400px;
  margin: 0 1rem;

  input {
      // background: ${({ theme }) => theme.color.background};
    font-size: ${({ theme }) => theme.font.M};
    font-weight: ${({ theme }) => theme.font.light};
    border: 1px solid rgba(0, 0, 0, 0.08);
    padding: 10px 43px;
    border-radius: 5px;
    width: 100%;
  }

  svg {
    position: absolute;
    left: 15px;
    top: 14px;
  }

  .spinner_wrapper {
    position: absolute;
    right: 15px;
    top: 14px;
    height: 15px;
    width: 15px;
  }

  @media (max-width: 1500px) {
    display: none;
  }
`;

const StyledHeart = styled.div`
  height: 18px;
  margin-right: 10px;
  display: block;
  position: relative;

  svg {
    width: 18px;
    height: 18px;
    margin: 0px !important;
  }

  span {
    position: absolute;
    right: -8px;
    top: -8px;
    height: 15px;
    width: 15px;
    background: ${({ theme }) => theme.color.primary};
    color: white;
    font-size: 10px;
    line-height: 10px;
    display: block;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
  }
`;

const MobileButtonsWrapper = styled.div`
  display: none;
  align-items: center;
  grid-gap: ${({ theme }) => theme.space.S};
  height: 30px;

  @media (max-width: 1200px) {
    display: flex;
  }
`;

const MobileNav = styled.div<{
  show?: boolean
}>`
  position: fixed;
  width: 100vw;
  height: 100vh;
  left: 0px;
  top: 0px;
  background: white;
  z-index: 9;
  transform: translateX(-100%);
  transition: all 0.3s;
  padding-top: 75px;

  ${({ show }) =>
          show &&
          css`
            transform: translateX(0%);
          `};

  > div {
    margin: 0px;
    border-top: 1px solid ${({ theme }) => theme.color.fourth};
  }
`;

const Nav = styled.div`
  display: flex;
  justify-content: space-between;
  grid-gap: ${({ theme }) => theme.space.M};
  align-items: center;
  margin: 0 0px;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const Logo = styled.div`
  height: 45px;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;

  a {
    display: block;
    height: 200px;
    width: 100%;
  }

  img {
    display: block;
  }
`;

const Content = styled.div`
  width: 90%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  //position: relative;
  z-index: 2;
`;

const Wrapper = styled.div<{
  isScrolled?: boolean
  show?: boolean
}>`
  width: 100%;
  //background: white;
  //box-shadow: 0px 5px 10px 0px rgba(0, 0, 0, 0.05);
  z-index: 10;
  position: fixed;
  left: 0px;
  top: 0px;
  transition: background-color 0.3s, transform 0.3s, padding 0.3s;
  transform: translateY(-100%);
  padding: 15px 0px;

  ${({ isScrolled }) =>
          isScrolled &&
          css`
            background-color: white;
          `}

  ${({ show }) =>
          show &&
          css`
            transform: translateY(0);
          `}
`;
